// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the deterministic game state. The committed actions are take(id) and draw(): take an
// EXPOSED stone whose rune is one step (wrapped) from the hand — it becomes the new hand and
// builds the heat multiplier — or draw from the scree, which reseeds the hand but RESETS heat and
// spends one of a limited number of draws. Because every cairn is a pure function of the seed and
// these actions are exact (no Math.random), the same cairn + the same action sequence is
// byte-identical on every machine — which makes the same-seed race fair by construction and the
// score honest. tick() advances the wall-clock (used solely by Cascade's throughput timer).

import { adjacent, buildBoard, type Action, type Board } from './board';
import type { Mode } from './modes';

export const BASE = 10; // base points per take
export const HEAT_CAP = 6; // the multiplier tops out here
export const CLEAR_BONUS = 100; // bonus for clearing a whole cairn

export type GameEvent =
  | { t: 'take'; id: number; rank: number; heat: number }
  | { t: 'draw'; rank: number }
  | { t: 'reject'; id: number } // an illegal tap — feedback only, no state change
  | { t: 'chain'; len: number } // the chain grew (combo pop)
  | { t: 'clearCairn' } // a cairn was fully cleared
  | { t: 'next'; index: number } // Cascade dealt a fresh cairn
  | { t: 'win' } // single-mode full clear, or Cascade clock completion
  | { t: 'buried' } // Scree: out of draws with stones left (a loss)
  | { t: 'end' } // Ramble: stuck with no draws (a soft, non-loss end)
  | { t: 'timeout' }; // Cascade: the clock ended

export interface GameState {
  mode: Mode;
  seed: number;
  index: number; // which cairn in a stream (0 for single-cairn modes)
  board: Board;
  present: boolean[]; // present[id] — is stone id still on the cairn
  hand: number; // the current hand rune (0..12)
  hue: number; // hue of the current hand stone (render only)
  stockIdx: number; // next scree index to draw (1..)
  draws: number; // draws used so far
  heat: number; // the chain multiplier, 1..HEAT_CAP
  chainLen: number; // the current unbroken chain length
  bestChain: number; // longest chain this run
  cleared: number; // stones removed from the CURRENT cairn
  totalCleared: number; // stones removed across the whole run
  cairns: number; // cairns fully cleared (Cascade banks these)
  score: number;
  elapsedMs: number;
  over: boolean;
  finished: boolean; // ended in success (full clear, or Cascade completed)
  reason: 'win' | 'timeout' | 'buried' | 'end' | null;
  events: GameEvent[];
}

function load(s: GameState, index: number): void {
  const b = buildBoard(s.mode, s.seed, index);
  s.index = index;
  s.board = b;
  s.present = new Array<boolean>(b.n).fill(true);
  s.hand = b.stock[0];
  s.hue = 0;
  s.stockIdx = 1;
  s.draws = 0;
  s.heat = 1;
  s.chainLen = 0;
  s.cleared = 0;
}

export function createGame(mode: Mode, seed: number): GameState {
  const b = buildBoard(mode, seed, 0);
  return {
    mode,
    seed: seed >>> 0,
    index: 0,
    board: b,
    present: new Array<boolean>(b.n).fill(true),
    hand: b.stock[0],
    hue: 0,
    stockIdx: 1,
    draws: 0,
    heat: 1,
    chainLen: 0,
    bestChain: 0,
    cleared: 0,
    totalCleared: 0,
    cairns: 0,
    score: 0,
    elapsedMs: 0,
    over: false,
    finished: false,
    reason: null,
    events: [],
  };
}

export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return [];
  const out = s.events;
  s.events = [];
  return out;
}

/** Stones still on the current cairn. */
export function remaining(s: GameState): number {
  let n = 0;
  for (let i = 0; i < s.present.length; i++) if (s.present[i]) n++;
  return n;
}

/** Is stone `id` exposed (nothing covering it remains)? */
export function isExposed(s: GameState, id: number): boolean {
  if (!s.present[id]) return false;
  return s.board.coveredBy[id].every((c) => !s.present[c]);
}

/** All exposed stone ids (takeable or not). */
export function exposedIds(s: GameState): number[] {
  const out: number[] = [];
  for (let id = 0; id < s.present.length; id++) if (isExposed(s, id)) out.push(id);
  return out;
}

/** Can stone `id` be taken right now (exposed AND rune adjacent to the hand)? */
export function canTake(s: GameState, id: number): boolean {
  return isExposed(s, id) && adjacent(s.board.stones[id].rank, s.hand);
}

/** Exposed stones adjacent to the hand — the legal takes. */
export function legalTakes(s: GameState): number[] {
  return exposedIds(s).filter((id) => adjacent(s.board.stones[id].rank, s.hand));
}

/** Is a draw currently available (budget + scree left)? */
export function canDraw(s: GameState): boolean {
  return !s.over && s.draws < s.board.drawBudget && s.stockIdx < s.board.stock.length;
}

/** Draws left in the budget (for the HUD). */
export function drawsLeft(s: GameState): number {
  return Math.max(0, Math.min(s.board.drawBudget - s.draws, s.board.stock.length - s.stockIdx));
}

function checkStuck(s: GameState): void {
  if (s.over || remaining(s) === 0) return;
  if (legalTakes(s).length > 0 || canDraw(s)) return;
  // no legal take and no draw left — resolve by mode
  if (s.mode.stream) {
    resolveNext(s); // Cascade: abandon the stuck cairn for a fresh one
  } else if (s.mode.lethal) {
    s.over = true;
    s.finished = false;
    s.reason = 'buried';
    s.events.push({ t: 'buried' });
  } else {
    s.over = true;
    s.finished = false;
    s.reason = 'end';
    s.events.push({ t: 'end' });
  }
}

function resolveNext(s: GameState): void {
  load(s, s.index + 1);
  s.events.push({ t: 'next', index: s.index });
}

function resolveClear(s: GameState): void {
  s.cairns++;
  s.score += CLEAR_BONUS;
  s.events.push({ t: 'clearCairn' });
  if (s.mode.stream) {
    resolveNext(s);
  } else {
    s.over = true;
    s.finished = true;
    s.reason = 'win';
    s.events.push({ t: 'win' });
  }
}

/**
 * Take stone `id` onto the hand. Legal iff it is exposed and its rune is one step (wrapped) from
 * the hand; an illegal or blocked tap is a no-op with a `reject` event (feedback only). A take
 * scores BASE × heat, grows the chain (heat up to HEAT_CAP), and clears the cairn if it was the
 * last stone.
 */
export function take(s: GameState, id: number): void {
  if (s.over) return;
  if (id < 0 || id >= s.present.length || !s.present[id]) return;
  if (!canTake(s, id)) {
    s.events.push({ t: 'reject', id });
    return;
  }
  s.present[id] = false;
  const stone = s.board.stones[id];
  s.hand = stone.rank;
  s.hue = stone.hue;
  s.score += BASE * s.heat;
  s.chainLen++;
  s.cleared++;
  s.totalCleared++;
  s.events.push({ t: 'take', id, rank: stone.rank, heat: s.heat });
  s.heat = Math.min(s.heat + 1, HEAT_CAP);
  if (s.chainLen > s.bestChain) s.bestChain = s.chainLen;
  s.events.push({ t: 'chain', len: s.chainLen });

  if (remaining(s) === 0) resolveClear(s);
  else checkStuck(s);
}

/** Draw the next scree stone onto the hand: reseeds the hand, RESETS heat, spends one draw. */
export function draw(s: GameState): void {
  if (s.over) return;
  if (!canDraw(s)) {
    s.events.push({ t: 'reject', id: -1 });
    return;
  }
  const rank = s.board.stock[s.stockIdx++];
  s.draws++;
  s.hand = rank;
  s.hue = s.draws % 4;
  s.heat = 1;
  s.chainLen = 0;
  s.events.push({ t: 'draw', rank });
  checkStuck(s);
}

/** Apply a solution/replay Action through the real take/draw path. */
export function applyAction(s: GameState, a: Action): void {
  if (a.k === 'draw') draw(s);
  else take(s, a.id);
}

/** Advance the wall-clock by `dtMs`. Only Cascade's clock can end a run this way. */
export function tick(s: GameState, dtMs: number): void {
  if (s.over) return;
  s.elapsedMs += Math.max(0, dtMs);
  if (s.mode.limitMs > 0 && s.elapsedMs >= s.mode.limitMs) {
    s.elapsedMs = s.mode.limitMs;
    s.over = true;
    s.finished = true;
    s.reason = 'timeout';
    s.events.push({ t: 'timeout' });
  }
}
